import { FormEvent, useRef, useState } from "react";
import { initiateQuote } from "../api";
import { pushEvent } from "../gtm";
import type { FinishState, Mode, Purpose, QuoteData } from "../types";
import uliceData from "../data/ulice_warszawa.json";
import StreetCombobox from "../components/StreetCombobox";

const ULICE_WARSZAWA: string[] = (uliceData as { ulice: string[] }).ulice;

const PURPOSES: { value: Purpose; label: string }[] = [
  { value: "przygotowuje_sprzedaz", label: "Przygotowuję się do sprzedaży" },
  { value: "sprawdzam_wartosc", label: "Sprawdzam wartość nieruchomości" },
  { value: "posrednik", label: "Jestem pośrednikiem nieruchomości" },
];

const FINISH_STATES: { value: FinishState; label: string }[] = [
  { value: "do_remontu", label: "Do remontu" },
  { value: "do_zamieszkania", label: "Do zamieszkania" },
  { value: "deweloperski", label: "W stanie deweloperskim" },
];

interface Props {
  partner: string;
  onDone: (data: QuoteData, quoteId: string) => void;
}

export default function Step1Area({ partner, onDone }: Props) {
  const [mode, setMode] = useState<Mode>("warszawa");
  const [cityName, setCityName] = useState("");
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [area, setArea] = useState("");
  const [rooms, setRooms] = useState("");
  const [floor, setFloor] = useState("");
  const [finishState, setFinishState] = useState<FinishState | "">("");
  const [purpose, setPurpose] = useState<Purpose | "">("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startTracked = useRef(false);

  function trackFormStart() {
    if (startTracked.current) return;
    startTracked.current = true;
    pushEvent("valuation_start", { partner });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const areaNum = parseFloat(area.replace(",", "."));
    if (isNaN(areaNum) || areaNum < 15 || areaNum > 150) {
      setError("Podaj metraż między 15 a 150 m²");
      return;
    }
    if (!street.trim() || !houseNumber.trim()) {
      setError("Wpisz ulicę i numer domu");
      return;
    }
    if (mode === "inne" && !cityName.trim()) {
      setError("Wpisz miasto");
      return;
    }
    if (!purpose) {
      setError("Wybierz cel wyceny");
      return;
    }

    const roomsNum = rooms.trim() ? parseInt(rooms, 10) : undefined;
    const floorNum = floor.trim() ? parseInt(floor, 10) : undefined;
    if (roomsNum !== undefined && (isNaN(roomsNum) || roomsNum < 1 || roomsNum > 6)) {
      setError("Liczba pokoi: 1–6");
      return;
    }
    if (floorNum !== undefined && (isNaN(floorNum) || floorNum < 0 || floorNum > 20)) {
      setError("Piętro: 0–20");
      return;
    }

    const data: QuoteData = {
      mode,
      area_m2: areaNum,
      rooms: roomsNum,
      floor: floorNum,
      finish_state: finishState || undefined,
      purpose,
      city_name: mode === "inne" ? cityName.trim() : undefined,
      street: street.trim(),
      house_number: houseNumber.trim(),
    };

    setLoading(true);
    try {
      const resp = await initiateQuote(partner, data);
      onDone(data, resp.quote_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wystąpił błąd");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} onFocusCapture={trackFormStart} className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-brand">Wycena mieszkania</h1>
        <p className="text-base font-medium text-gray-800 mt-2">
          Krok do silnej pozycji w negocjacjach z kupującym. Zaczynamy?
        </p>
        <p className="text-sm text-gray-600 mt-1">
          Podaj dane mieszkania — otrzymasz bezpłatną wycenę nieruchomości.{" "}
          <strong className="text-gray-800">Wynik zobaczysz od razu online.</strong>
        </p>
      </header>

      <div className="flex rounded-lg bg-gray-100 p-1">
        <button
          type="button"
          onClick={() => setMode("warszawa")}
          className={`flex-1 py-2 text-sm font-medium rounded-md transition ${
            mode === "warszawa" ? "bg-white shadow text-brand" : "text-gray-600"
          }`}
        >
          Warszawa
        </button>
        <button
          type="button"
          onClick={() => setMode("inne")}
          className={`flex-1 py-2 text-sm font-medium rounded-md transition ${
            mode === "inne" ? "bg-white shadow text-brand" : "text-gray-600"
          }`}
        >
          Inne miasto
        </button>
      </div>

      {mode === "inne" && (
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Miasto</span>
          <input
            type="text"
            value={cityName}
            onChange={(e) => setCityName(e.target.value)}
            placeholder="np. Kraków"
            className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none"
            required
          />
        </label>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label htmlFor="street-input" className="text-sm font-medium text-gray-700 block mb-1">
            Ulica
          </label>
          {mode === "warszawa" ? (
            <StreetCombobox
              id="street-input"
              value={street}
              onChange={setStreet}
              options={ULICE_WARSZAWA}
              placeholder="np. Marszałkowska"
            />
          ) : (
            <input
              id="street-input"
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="np. Floriańska"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none"
              required
            />
          )}
        </div>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Nr domu</span>
          <input
            type="text"
            value={houseNumber}
            onChange={(e) => setHouseNumber(e.target.value)}
            placeholder="12A"
            className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none"
            required
          />
        </label>
      </div>

      <p className="-mt-3 text-xs text-gray-500">
        Adres jest potrzebny wyłącznie do dopasowania cen transakcyjnych w najbliższej
        okolicy.
      </p>

      <div className="grid grid-cols-3 gap-3">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Metraż (m²)</span>
          <input
            type="text"
            inputMode="decimal"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="np. 52.5"
            className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none"
            required
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Liczba pokoi</span>
          <input
            type="number"
            min={1}
            max={6}
            value={rooms}
            onChange={(e) => setRooms(e.target.value)}
            className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Piętro</span>
          <select
            value={floor}
            onChange={(e) => setFloor(e.target.value)}
            className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none bg-white"
          >
            <option value="">Wybierz…</option>
            <option value="0">Parter</option>
            {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={String(n)}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">Stan wykończenia</span>
        <select
          value={finishState}
          onChange={(e) => setFinishState(e.target.value as FinishState | "")}
          className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none bg-white"
        >
          <option value="">Wybierz…</option>
          {FINISH_STATES.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">Cel wyceny</span>
        <select
          value={purpose}
          onChange={(e) => setPurpose(e.target.value as Purpose | "")}
          className="mt-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand outline-none bg-white"
        >
          <option value="">Wybierz…</option>
          {PURPOSES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-brand text-white rounded-lg font-semibold hover:bg-brand-600 disabled:opacity-50 transition"
      >
        {loading ? "Liczę…" : "Wyceń mieszkanie"}
      </button>

      <p className="text-xs text-gray-500 text-center">Wycena orientacyjna.</p>
    </form>
  );
}
